import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { axiosBuzzOne } from '../../../axios';

const moduleName = 'featureFlags';

/**
 * Talks to buzzone-api's BCC flag surface. Every route here requires a BCC principal
 * holding the `featureFlags` scope — today only `bccSuperAdmin` has it, via the `all`
 * short-circuit, so a 403 from any of these almost certainly means the signed-in role
 * has not been granted `featureFlags:readWrite` in rolePermission.config.ts.
 */

export const getFeatureFlagTree = createAsyncThunk(
  `GET/${moduleName}/tree`,
  async (_payload, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'GET',
        url: '/feature-flags/tree',
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

export const getFeatureFlagAudit = createAsyncThunk(
  `GET/${moduleName}/audit`,
  async (key, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'GET',
        url: `/feature-flags/${encodeURIComponent(key)}/audit`,
      });
      return { key, entries: response.data };
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

/**
 * Creates the database row for one flag the catalog already declares.
 *
 * `key` is not free-form: identity, owner and removeBy live in the API's `flag.catalog.ts`
 * so that a typo is a compile error rather than a flag that quietly resolves to `false`
 * forever. An undeclared key comes back as a 400 naming every unknown key — surfaced
 * verbatim, because the fix is a backend PR, not a retry.
 *
 * `propagate` fans the *row* out to the other environments. It does not carry release
 * state: each environment computes its own, so a flag created from staging lands in
 * production OFF. That is the mechanism, not a gap to work around.
 *
 * Note the API returns 200 here, not 201 — a global interceptor rewrites it — so nothing
 * downstream may branch on the status code.
 */
export const createFeatureFlag = createAsyncThunk(
  `POST/${moduleName}/create`,
  async ({ key, propagate, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'POST',
        url: '/feature-flags',
        data: { key, propagate: Boolean(propagate), reason: reason || undefined },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

/**
 * Reconciles catalog against database — the whole catalog when `keys` is omitted.
 *
 * `dryRun` is always sent explicitly. It defaults to `true` server-side, which is the safe
 * default but a bad thing to rely on from here: the screen's two steps are "get the plan"
 * and "apply the plan", and each has to say which one it means.
 *
 * Idempotent, so re-running after a partial failure is the correct fix rather than a risk.
 */
export const syncFeatureFlags = createAsyncThunk(
  `POST/${moduleName}/sync`,
  async ({ keys, dryRun, propagate, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'POST',
        url: '/feature-flags/sync',
        data: {
          keys: keys && keys.length ? keys : undefined,
          dryRun: Boolean(dryRun),
          propagate: Boolean(propagate),
          reason: reason || undefined,
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

export const updateFlagRelease = createAsyncThunk(
  `PUT/${moduleName}/release`,
  async ({ key, data }, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'PUT',
        url: `/feature-flags/${encodeURIComponent(key)}/release`,
        data,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

export const updateFlagAvailability = createAsyncThunk(
  `PUT/${moduleName}/availability`,
  async ({ key, data }, { rejectWithValue }) => {
    try {
      const response = await axiosBuzzOne({
        method: 'PUT',
        url: `/feature-flags/${encodeURIComponent(key)}/availability`,
        data,
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response && error.response.data);
    }
  }
);

const featureFlagsSlice = createSlice({
  name: moduleName,
  initialState: {
    tree: [],
    loading: false,
    error: null,
    audit: {},
    auditLoading: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getFeatureFlagTree.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getFeatureFlagTree.fulfilled, (state, action) => {
        state.loading = false;
        state.tree = action.payload || [];
      })
      .addCase(getFeatureFlagTree.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload && action.payload.message) || 'Unable to load feature flags';
      })
      .addCase(getFeatureFlagAudit.pending, (state) => {
        state.auditLoading = true;
      })
      .addCase(getFeatureFlagAudit.fulfilled, (state, action) => {
        state.auditLoading = false;
        state.audit[action.payload.key] = action.payload.entries || [];
      })
      .addCase(getFeatureFlagAudit.rejected, (state) => {
        state.auditLoading = false;
      });
    // Mutations deliberately do not patch `tree` from their response. The screen
    // refetches instead: a write changes ancestor gating for descendants too, and
    // recomputing that client-side would duplicate logic the backend already owns
    // — and would eventually disagree with it.
  },
});

export default featureFlagsSlice.reducer;
